/* Drill player (02 §2.2, §3.3.2).
 *
 * Front → Reveal → back + source note → one of four grades. One tap per grade,
 * next Card immediately. No confidence capture on drills — that is deliberate
 * (flow preservation); confidence belongs to Quizzes.
 *
 * Two rules are easy to fake and are implemented honestly here:
 *
 *  1. The answer is written to IndexedDB and AWAITED before the UI advances. A
 *     kill mid-session loses the render, never the grade (03 §3.7).
 *  2. "Again" re-enters the SAME session after >= 10 intervening Cards or
 *     >= 10 minutes, whichever comes first, and must be answered again before
 *     the session counts as cleared.
 *
 * The source note is rendered from the owning deck's `provenance` envelope —
 * there is no per-Card provenance field, by design (04 §1.3).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AGAIN_REENTRY_MIN_CARDS, AGAIN_REENTRY_MIN_MS, DRILL_ROUND_SIZE,
} from '@ascent/shared';
import type { QueueEntry, UnitDto } from '../api';
import { cachedUnit, queueReview } from '../sync';
import { Markdown } from '../markdown';
import { CodeRunner, type ExerciseSpec } from '../exercise/code-runner';
import { pythonRunnable } from '../exercise/pack';
import { planRounds, playableCards, roundOf } from '../exercise/rotation';
import type { RunOutcome } from '../exercise/types';

interface DrillCard {
  id: string;
  unitId: string;
  front_md: string;
  back_md: string;
  hint: string | null;
  /** Present on exercise Cards — makes this a code Card in rotation (02 §2.8). */
  exercise?: ExerciseSpec;
}


interface Recycled {
  card: DrillCard;
  /** Answer count at the moment it was graded Again. */
  answeredAt: number;
  atMs: number;
}

const GRADES = [
  { grade: 1 as const, label: 'Again', tone: 'critical' },
  { grade: 2 as const, label: 'Hard', tone: 'serious' },
  { grade: 3 as const, label: 'Good', tone: 'good' },
  { grade: 4 as const, label: 'Easy', tone: 'muted' },
];

export function DrillScreen({
  queue, onDone, onOpenSettings,
}: {
  queue: QueueEntry[];
  onDone: (answered: number) => void;
  onOpenSettings?: () => void;
}) {
  const [cards, setCards] = useState<DrillCard[] | null>(null);
  const [roundStarts, setRoundStarts] = useState<number[]>([]);
  const [units, setUnits] = useState<Map<string, UnitDto>>(new Map());
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [answered, setAnswered] = useState(0);
  const [recycled, setRecycled] = useState<Recycled[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [suggestedGrade, setSuggestedGrade] = useState<1 | 3 | null>(null);
  const [skippedExercises, setSkippedExercises] = useState(0);
  const shownAt = useRef<number>(Date.now());

  // Resolve Card content from the CACHE — this is what makes the session work
  // in airplane mode. A Unit that was never prefetched simply drops out.
  useEffect(() => {
    void (async () => {
      const resolved: DrillCard[] = [];
      const unitMap = new Map<string, UnitDto>();

      for (const entry of queue) {
        let unit = unitMap.get(entry.unit_id);
        if (!unit) {
          const fetched = await cachedUnit(entry.unit_id);
          if (!fetched) continue;
          unit = fetched;
          unitMap.set(entry.unit_id, unit);
        }
        const deck = unit as unknown as { cards?: DrillCard[] };
        const card = deck.cards?.find((c) => c.id === entry.card_id);
        if (card) {
          resolved.push({ ...card, unitId: entry.unit_id });
        }
      }

      const playable = playableCards(resolved, await pythonRunnable());
      setSkippedExercises(resolved.length - playable.length);

      const planned = planRounds(playable);
      setUnits(unitMap);
      setCards(planned.cards);
      setRoundStarts(planned.starts);
      if (playable.length === 0 && queue.length > 0) {
        setLoadError(
          resolved.length > 0
            ? 'Only Python exercises are due, and this device has no offline pack. Install it in Settings, or connect once.'
            : 'These Cards are not cached yet. Connect once to download them.',
        );
      }
    })();
  }, [queue]);

  /** A recycled Card becomes playable after 10 intervening answers OR 10 minutes. */
  const dueRecycled = useMemo(
    () =>
      recycled.filter(
        (r) =>
          answered - r.answeredAt >= AGAIN_REENTRY_MIN_CARDS ||
          Date.now() - r.atMs >= AGAIN_REENTRY_MIN_MS,
      ),
    [recycled, answered],
  );

  const remainingFresh = cards ? cards.length - index : 0;
  const current: DrillCard | null =
    cards === null
      ? null
      : index < cards.length
        ? cards[index]!
        : dueRecycled[0]?.card ?? null;

  // The session is clear only when the fresh queue AND every recycled Card are done.
  const sessionClear = cards !== null && index >= cards.length && recycled.length === 0;

  useEffect(() => {
    shownAt.current = Date.now();
    setRevealed(false);
    setShowHint(false);
    setSuggestedGrade(null);
  }, [current?.id]);

  /* Running an exercise reveals the Card and preselects a grade from the check
   * outcome — fail → Again, pass → Good (02 §2.2). Preselected, never forced:
   * the learner still taps, and can tap a different one. A timeout or a blocked
   * runtime suggests nothing; that is a tooling outcome, not evidence about
   * whether they know the material. */
  const onExerciseOutcome = useCallback((outcome: RunOutcome) => {
    if (outcome.status !== 'ok') return;
    setRevealed(true);
    setSuggestedGrade(outcome.passed ? 3 : 1);
  }, []);

  const grade = useCallback(
    async (value: 1 | 2 | 3 | 4) => {
      if (!current || busy) return;
      setBusy(true);
      try {
        // AWAIT the durable write before advancing — the whole point.
        await queueReview(current.id, current.unitId, value, Date.now() - shownAt.current);

        const fromFresh = cards !== null && index < cards.length;
        const nextAnswered = answered + 1;

        setRecycled((prev) => {
          const withoutCurrent = prev.filter((r) => r.card.id !== current.id);
          return value === 1
            ? [...withoutCurrent, { card: current, answeredAt: nextAnswered, atMs: Date.now() }]
            : withoutCurrent;
        });

        setAnswered(nextAnswered);
        if (fromFresh) setIndex((i) => i + 1);
      } finally {
        setBusy(false);
      }
    },
    [current, busy, cards, index, answered],
  );

  if (cards === null) {
    return (
      <main className="screen">
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--block" />
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="screen">
        <p className="eyebrow">Drills</p>
        <h1>Nothing to play offline</h1>
        <p>{loadError}</p>
        <button type="button" onClick={() => onDone(answered)}>Back to Today</button>
      </main>
    );
  }

  if (!current || sessionClear) {
    return (
      <main className="screen">
        <p className="eyebrow">Session complete</p>
        <h1>{answered} {answered === 1 ? 'card' : 'cards'} answered</h1>
        <p>
          {answered === 0
            ? 'Nothing was due.'
            : 'Your answers are saved locally and sync as soon as you are online. Scheduling is recomputed on the server — the app never invents a due date.'}
        </p>
        {skippedExercises > 0 ? (
          <p className="hint">
            {skippedExercises} Python {skippedExercises === 1 ? 'exercise was' : 'exercises were'} held
            back — no offline pack on this device. {skippedExercises === 1 ? 'It comes' : 'They come'} back
            unchanged; nothing was recorded against {skippedExercises === 1 ? 'it' : 'them'}.
          </p>
        ) : null}
        <button type="button" onClick={() => onDone(answered)}>Back to Today</button>
      </main>
    );
  }

  // Rounds come from the real boundaries, not from `answered / 20`: a round is
  // not always 20 long once exercise Cards spill into extra rounds (11 §4.6),
  // and recycled Cards inflate `answered` without advancing the queue.
  const round = roundOf(roundStarts, Math.min(index, cards.length - 1));
  const rounds = Math.max(1, roundStarts.length);
  const left = remainingFresh + recycled.length;
  const deck = units.get(current.unitId);
  const source = deck?.provenance.sources[0];

  return (
    <main className="screen drill">
      {/* Leaving early costs nothing: every grade was awaited into IndexedDB
       * before the next Card appeared (03 §3.7), so there is no confirm step. */}
      <button type="button" className="back" onClick={() => onDone(answered)}>← Today</button>

      <p className="eyebrow">
        Round {round} of {rounds} · {left} left
        {recycled.length > 0 ? ` · ${recycled.length} to repeat` : ''}
      </p>

      <div className="drill__card">
        <div className="drill__front">
          <Markdown source={current.front_md} />
        </div>

        {current.exercise ? (
          <CodeRunner
            key={current.id}
            exercise={current.exercise}
            onOutcome={onExerciseOutcome}
            onOpenSettings={onOpenSettings}
          />
        ) : null}

        {!revealed && !current.exercise && current.hint ? (
          showHint ? (
            <p className="drill__hint"><Markdown source={current.hint} /></p>
          ) : (
            <button type="button" className="drill__hintbtn" onClick={() => setShowHint(true)}>
              Show hint
            </button>
          )
        ) : null}

        {revealed ? (
          <>
            <hr className="drill__rule" />
            <div className="drill__back">
              <Markdown source={current.back_md} />
            </div>
            {source ? (
              <p className="drill__source">
                Source: <code>{source.ref}</code>
                {source.note ? ` — ${source.note}` : ''}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="drill__actions">
        {revealed ? (
          <>
            {suggestedGrade !== null ? (
              <p className="hint">
                {suggestedGrade === 3
                  ? 'Checks passed — Good is preselected.'
                  : 'Checks failed — Again is preselected.'}
                {' '}Tap a different grade if you disagree; you own this call.
              </p>
            ) : null}
            <div className="grades">
              {GRADES.map((g) => (
                <button
                  key={g.grade}
                  type="button"
                  className={`grade grade--${g.tone} ${suggestedGrade === g.grade ? 'grade--suggested' : ''}`}
                  disabled={busy}
                  onClick={() => void grade(g.grade)}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </>
        ) : current.exercise ? (
          <p className="hint">Run your code to see the checks and grade this Card.</p>
        ) : (
          <button type="button" onClick={() => setRevealed(true)}>Reveal</button>
        )}
      </div>
    </main>
  );
}
